import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { getIn, useFormikContext } from 'formik';

import { appConfig } from 'brand-config';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import useSession from 'core/hooks/use-session';
import { CoverageList } from 'common/components/home/coverages/additionals/coverage-list';
import PurchaseOffSite from 'common/components/home/coverages/purchase-off-site';
import { isMobileHome, isModularHome } from '../risk-types/property-type-helpers';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import useStyles from './sub-risk.styles';

// Dwelling (residence) risk types, in the same order the farm rater scans them to pick the
// primary residence for the Palomar EQ quote — see palomar.repository `findPrimaryFarmDwelling`.
// The Palomar quote (and its caseLink) is per-offer and covers the primary dwelling, so we only
// surface the UI on that one risk.
const DWELLING_RISK_TYPES = ['FO1', 'FO2', 'FO3', 'FO4'];

// Manufactured (mobile/modular) dwellings aren't eligible for Palomar's site-built FlexChoice EQ,
// so they're skipped here too — kept in lockstep with the rater's findPrimaryFarmDwelling.
const getPrimaryDwellingRiskId = (farmLocations) => {
  for (const location of farmLocations || []) {
    const risk = (location?.risks || []).find(
      (r) =>
        DWELLING_RISK_TYPES.includes(r?.type) &&
        r?.coverages?.coverageA &&
        !isMobileHome(r?.building) &&
        !isModularHome(r?.building)
    );
    if (risk) {
      return risk.id;
    }
  }
  return undefined;
};

/**
 * Palomar earthquake coverage for Forreston farm products (Home/Rental/Farm). Earthquake is
 * written separately by Palomar; here we show the priced deductible options returned by the rater
 * (RateOption.eqRates) on the primary dwelling risk, and — once the Branch policy is bound — a
 * "buy through Palomar" caseLink plus a field to record the resulting Palomar policy id (mirrors
 * the homeowners flow in home/coverages/additionals). Gated to brands/states that opt in via
 * `farm[state].featureFlags.palomarEQOffered` (Forreston IL). See #33499.
 */
const EarthquakePalomarCoverageForm = ({ fieldName, state }) => {
  const classes = useStyles();
  const { values } = useFormikContext();
  const { offer: offerStore, account: accountStore } = useStore();
  const { inPolicyRoute } = useRouteHelper();
  const session = useSession();
  const toast = useToast();

  const policyStore = accountStore.policies.policy;
  const repEmail = session?.user?.email;

  // Offer route reads the live offer store; policy route reads the bound policy (offer store is
  // empty there). Both expose `.offer.options`; the selected farm option carries eqRates.
  const data = inPolicyRoute ? policyStore.policy : offerStore;
  const policyType = inPolicyRoute ? data?.policyType : data?.selectedOption;
  const option = data?.offer?.options?.find((o) => o.type === policyType) || {};
  const { eqRates } = option;

  const earthquakePolicyId = inPolicyRoute ? policyStore.policyDetails?.earthquakeCoverage?.policyId : null;
  const coverageA = getIn(values, `${fieldName}.coverages.coverageA`);
  const isEqRatesRetrieved = !!eqRates?.options?.length;

  const onViewEarthquakePolicy = useCallback(async () => {
    const { error } = await policyStore.getPalomarPolicyDoc(earthquakePolicyId, repEmail, 'eq');
    if (error) {
      toast.notify({ type: 'error', message: error });
    }
  }, [policyStore, earthquakePolicyId, repEmail, toast]);

  const palomarEQOffered = appConfig.farm?.[state]?.featureFlags?.palomarEQOffered;
  const isPrimaryDwelling = getIn(values, fieldName)?.id === getPrimaryDwellingRiskId(values.farmLocations);
  // Nothing to show until the rater returns Palomar options (or the EQ policy was already bought).
  const hasSomethingToShow = (isEqRatesRetrieved && !!coverageA) || !!earthquakePolicyId;

  if (!palomarEQOffered || !isPrimaryDwelling || !hasSomethingToShow) {
    return null;
  }

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Earthquake coverage (Palomar)</div>}
      initialOpen={false}
    >
      <Grid container spacing={2}>
        {!earthquakePolicyId && (
          <NotificationCard type="quaternary">
            <Grid container alignItems="center">
              Earthquake coverage is handled separately from this policy. It is purchased through our partner Palomar
              after the policy is bound.
            </Grid>
          </NotificationCard>
        )}
        {(!inPolicyRoute || !earthquakePolicyId) && isEqRatesRetrieved && !!coverageA && (
          <CoverageList data={eqRates.options} coverageA={coverageA} />
        )}
        {inPolicyRoute && isEqRatesRetrieved && eqRates?.caseLink && (
          <PurchaseOffSite
            name="earthquakeCoverage.policyId"
            partnerName="Palomar"
            purchaseLink={eqRates.caseLink}
            onViewPolicy={onViewEarthquakePolicy}
            policyId={earthquakePolicyId}
          />
        )}
        {inPolicyRoute && !isEqRatesRetrieved && !earthquakePolicyId && (
          <Label type="toast"> We couldn’t retrieve any data from Palomar</Label>
        )}
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

EarthquakePalomarCoverageForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  state: PropTypes.string
};

EarthquakePalomarCoverageForm.defaultProps = {
  state: undefined
};

export default observer(EarthquakePalomarCoverageForm);
